import { useIntl } from '@capsicum/common';
import { CountrySelect, Skeleton, SkeletonItem, TextInput } from '@capsicum/common-web';
import {
	type PostalAddressVerificationFormType,
	type Step1Props,
	usePostalAddressVerification
} from '@capsicum/features/regulations/shared';
import styled from 'styled-components';

export const Step1 = ({ allowChangeOfCountry, readOnly }: Step1Props) => {
	const { $t } = useIntl();

	const { isFormLoading, userAddress } = usePostalAddressVerification();

	return (
		<S.StepContainer>
			<S.Title>{$t('postalAddressVerification.title')}</S.Title>
			<S.InputsContainer>
				{isFormLoading ? (
					<S.SkeletonWrapper height={56}>
						{Array.from({ length: 5 }, (_, i) => (
							<S.SkeletonInput key={i} />
						))}
					</S.SkeletonWrapper>
				) : (
					<>
						<TextInput<PostalAddressVerificationFormType>
							autoCapitalize="none"
							disabled={readOnly}
							label={$t('kycLevel3PostalAddress.streetAddress')}
							name="street"
							testID="postal-address-verification-street-input"
						/>
						<CountrySelect<PostalAddressVerificationFormType>
							disabled={readOnly || (!allowChangeOfCountry && !!userAddress?.countryCode)}
							label={$t('global.country')}
							labelProp="name"
							name="country"
							testID="postal-address-verification-country-input"
							withSearch
						/>
						<TextInput<PostalAddressVerificationFormType>
							autoCapitalize="none"
							disabled={readOnly}
							label={$t('kycLevel3PostalAddress.city')}
							name="city"
							testID="postal-address-verification-city-input"
						/>
						<TextInput<PostalAddressVerificationFormType>
							autoCapitalize="none"
							disabled={readOnly}
							label={$t('kycLevel3PostalAddress.region')}
							name="region"
							testID="postal-address-verification-region-input"
						/>
						<TextInput<PostalAddressVerificationFormType>
							autoCapitalize="none"
							disabled={readOnly}
							label={$t('kycLevel3PostalAddress.postcode')}
							name="zipCode"
							testID="postal-address-verification-zipcode-input"
						/>
					</>
				)}
			</S.InputsContainer>
		</S.StepContainer>
	);
};

namespace S {
	export const StepContainer = styled.div`
		display: flex;
		flex-direction: column;
		gap: ${({ theme }) => theme.spacings.small};
	`;

	export const Title = styled.p`
		${({ theme }) => theme.fonts.headings.title5};
		color: ${({ theme }) => theme.colors.text.main.enabled};
		margin: 0;
	`;

	export const InputsContainer = styled.div`
		display: flex;
		flex-direction: column;
		gap: ${({ theme }) => theme.spacings.small};
	`;

	export const SkeletonWrapper = styled(Skeleton)`
		display: flex;
		flex-direction: column;
		gap: ${({ theme }) => theme.spacings.xsmall};
	`;

	export const SkeletonInput = styled(SkeletonItem)`
		border-radius: ${({ theme }) => theme.sizing.xxsmall};
		margin-bottom: ${({ theme }) => theme.spacings.xxsmall};
	`;
}
